import React, { useState, useEffect, useCallback } from 'react';
import styles from './NarrativePage.module.scss';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { extractPlatformName } from '../../utils/extractPlatformName';
import { ReactComponent as LinkIcon } from '../../assets/link.svg';
import { ReactComponent as AnchorIcon } from '../../assets/anchor.svg';
import { ReactComponent as UpdateIcon } from '../../assets/update.svg';
import { API, ShareAPI } from '../../API';
import {
  mapThreatsStatus,
  mapThreatsTypesConst,
  mapThreatsSubjects,
} from '../../utils/threats';
import { formatDistanceToNow, format } from 'date-fns';
import { enGB as en, uk } from 'date-fns/locale';
import { formatQuery } from '../../utils/formatQuery';
import { showRequestError } from '../../utils/requestError';
import { toUTCDateString } from '../../utils/utcDateString';

const mapThreatStatusClasses = {
  ACCEPTED: styles.accepted,
  PENDING: styles.pending,
  DECLINED: styles.declined,
};

type CaseParametersCardProps = {
  narrative: any | null;
  showPopulationStatus: boolean;
  isShare?: boolean;
};

interface InactiveNarrativeReasonMap {
  [key: string]: string;
}

interface StatusClassesMap {
  [key: string]: string;
}

interface PopulationStatusMap {
  [key: string]: string;
}

export const CaseParametersCard: React.FC<CaseParametersCardProps> = ({
  narrative,
  showPopulationStatus,
  isShare,
}) => {
  const { t } = useTranslation();
  const [updateTimeRaw, setUpdateTimeRaw] = useState<string | null>(null);
  const [nextUpdateTimeRaw, setNextUpdateTimeRaw] = useState<string | null>(
    null,
  );
  const [isUpdatable, setIsUpdatable] = useState<boolean>(true);

  useEffect(() => {
    if (!narrative) return;
    if (narrative?.is_updatable === false) {
      setIsUpdatable(false);
    } else {
      setIsUpdatable(true);
    }
  }, [narrative]);

  const isCaseManual =
    (narrative?.type === 'NARRATIVE' && narrative.is_manual === true) ||
    narrative?.type === 'MANUAL';

  const updateTerminationReason = () => {
    if (!narrative?.is_active) {
      return t('No updates scheduled: case is inactive');
    } else if (
      narrative?.is_active &&
      narrative?.parameters?.end_date &&
      narrative?.parameters?.end_date < new Date().toISOString()
    ) {
      return t('No updates scheduled: end date has passed');
    }
  };

  const getUserLocale = () => {
    const lang = localStorage.getItem('i18nextLng');
    if (
      lang === 'ua-UK' ||
      lang === 'ru-RU' ||
      lang === 'ru' ||
      lang === 'uk'
    ) {
      return uk;
    }
    return en;
  };

  const locale = getUserLocale();

  const parseUTCDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(toUTCDateString(dateString));
  };

  const updateTime = updateTimeRaw
    ? formatDistanceToNow(parseUTCDate(updateTimeRaw)!, { locale })
    : null;
  const nextUpdateTime =
    nextUpdateTimeRaw && isUpdatable
      ? format(parseUTCDate(nextUpdateTimeRaw)!, 'HH:mm')
      : null;
  const nextUpdateDate = nextUpdateTimeRaw
    ? format(parseUTCDate(nextUpdateTimeRaw)!, 'dd-MM-yyyy')
    : null;
  const firstPublicationDate = narrative?.first_message_date_publicated
    ? format(parseUTCDate(narrative.first_message_date_publicated)!, 'yyyy-MM-dd')
    : null;
  const lastPublicationDate = narrative?.last_message_date_publicated
    ? format(parseUTCDate(narrative.last_message_date_publicated)!, 'yyyy-MM-dd')
    : null;

  const nextUpdateTooltipId = 'NexTimeUpdateTooltip';
  const nextUpdateTooltipContent = nextUpdateTimeRaw
    ? isUpdatable
      ? t('Next update {{date}} at {{time}}', {
          date: nextUpdateDate || '-',
          time: nextUpdateTime || '-',
        })
      : updateTerminationReason()
    : t('Next update: N/A');

  const api = isShare ? ShareAPI : API;

  const isMoreThan24HoursAgo = (dateString: string | null): boolean => {
    if (!dateString) return false;
    const date = parseUTCDate(dateString);
    if (!date) return false;
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    return diffInHours > 24;
  };

  const showUpdatedTime = () => {
    if (!updateTimeRaw) return t('Updated: N/A');
    const localUpdateTime = parseUTCDate(updateTimeRaw);
    if (!localUpdateTime) return t('Updated: N/A');

    if (isMoreThan24HoursAgo(updateTimeRaw)) {
      return t('Updated: {{date}} at {{time}}', {
        date: format(localUpdateTime, 'dd-MM-yyyy', { locale }),
        time: format(localUpdateTime, 'HH:mm', { locale }),
      });
    } else if (updateTime) {
      return t('Updated: {{time}} ago', { time: updateTime });
    }
  };

  let showUpdatedTimeString = showUpdatedTime() || null;

  const getNarrativeUpdateTime = useCallback(
    (narrative: any) => {
      if (!narrative) {
        return;
      }
      api
        .fetch('GET', `/API/v1/narratives/${narrative?.id}/update-times`)
        .then((response: any) => {
          if (response.last_updated_at === null) {
            setUpdateTimeRaw(null);
          } else {
            setUpdateTimeRaw(response.last_updated_at);
          }

          if (response.next_update_at === null) {
            setNextUpdateTimeRaw(null);
          } else {
            setNextUpdateTimeRaw(response.next_update_at);
          }
        })
        .catch((error: any) => {
          setUpdateTimeRaw(null);
          setNextUpdateTimeRaw(null);
          showRequestError(error, t('Case parameters'));
        });
    },
    [api],
  );

  useEffect(() => {
    getNarrativeUpdateTime(narrative);
  }, [narrative, getNarrativeUpdateTime]);

  const mapInactiveNarrativeReason: InactiveNarrativeReasonMap = {
    'Trial expired': t('Workspace trial was ended'),
    'Expired case': t('No activity for over 30 days'),
    'Insufficient AI quota': t('AI quota was exhausted'),
    'Insufficient ANALYTICAL quota': t('Analytical quota was exhausted'),
    Manual: t('Deactivated by user'),
  };

  const mapStatusClasses: StatusClassesMap = {
    active: styles.active,
    inactive: styles.inactive,
  };

  const mapPopulationStatus: PopulationStatusMap = {
    POPULATING: t('Processing'),
    DONE: t('Completed'),
    FAILED: t('Failed'),
    NO_NEEDED: '-',
  };

  let isNarrativeActive = narrative.is_active === true ? 'active' : 'inactive';

  let caseType = null;

  if (
    (narrative?.type === 'NARRATIVE' && narrative.is_manual === true) ||
    narrative?.type === 'MANUAL'
  ) {
    caseType = t('Manual');
  } else if (narrative?.type === 'NARRATIVE') {
    caseType = t('Narrative');
  } else if (narrative.type === 'TOPIC_MODELING') {
    caseType = t('Topic modeling');
  }

  if (!narrative) {
    return null;
  }

  const keywordsString = Array.isArray(narrative?.parameters?.keywords)
    ? narrative.parameters.keywords.join(' ')
    : narrative?.parameters?.keywords || '';

  // Manual cases have no search period — fall back to the actual publication range
  const startDate =
    (isCaseManual && firstPublicationDate) ||
    narrative?.parameters?.start_date ||
    null;
  const endDate =
    (isCaseManual && lastPublicationDate) ||
    narrative?.parameters?.end_date ||
    null;

  if (narrative.is_threat) {
    return (
      <div className="report-section">
        <div className={styles.parametersCard}>
          <div className={styles.headerInfoTitleContainer}>
            <h3 className={styles.headerInfoTitle}>{narrative?.name}</h3>
            {showUpdatedTimeString && !isCaseManual ? (
              <div className={styles.updateTimeInfo}>
                <UpdateIcon
                  className={styles.updateIcon}
                  onClick={() => getNarrativeUpdateTime(narrative)}
                />
                {showUpdatedTimeString}
                <AnchorIcon
                  className={styles.nextUpdateHint}
                  data-tooltip-id={nextUpdateTooltipId}
                  data-tooltip-content={nextUpdateTooltipContent}
                />
              </div>
            ) : (
              ''
            )}
          </div>

          <div className={styles.headerInfo}>
            <div className={styles.headerList}>
              <div className={styles.parametersInfoItem}>
                <span>{t('Start date')}:</span>{' '}
                {firstPublicationDate ?? <>&mdash;</>}
              </div>
              <div className={styles.parametersInfoItem}>
                <span>{t('End date')}:</span>{' '}
                {lastPublicationDate ?? <>&mdash;</>}
              </div>
              <div className={styles.parametersInfoItem}>
                <span>{t('Status')}:</span>{' '}
                {narrative?.review_status ? (
                  <div
                    className={`${styles.statusWrapper} ${
                      mapThreatStatusClasses[
                        narrative?.review_status as keyof typeof mapThreatStatusClasses
                      ]
                    }`}
                  >
                    {t(
                      mapThreatsStatus[
                        narrative?.review_status as keyof typeof mapThreatsStatus
                      ],
                    )}
                  </div>
                ) : (
                  <>&ndash;</>
                )}
              </div>
            </div>
            <div className={styles.headerTags}>
              <div className={styles.tagsContainer}>
                {narrative?.incident_type?.map((tag: string, index: number) => {
                  return (
                    <span key={`${tag}${index}`} className={styles.badge}>
                      {t(
                        mapThreatsTypesConst[
                          tag as keyof typeof mapThreatsTypesConst
                        ],
                      )}
                    </span>
                  );
                })}
              </div>
              <div className={styles.tagsContainer}>
                {narrative?.topic?.map((subject: string, index: number) => {
                  return (
                    <span
                      key={`${subject}${index}`}
                      className={`${styles.mark}`}
                    >
                      {t(
                        mapThreatsSubjects[
                          subject as keyof typeof mapThreatsSubjects
                        ],
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <Tooltip
          id={nextUpdateTooltipId}
          place="top"
          style={{ textTransform: 'none', fontSize: '14px' }}
        />
      </div>
    );
  }

  return (
    <div className="report-section">
      <div className={styles.parametersCard}>
        <div className={styles.headerInfoTitleContainer}>
          <h3 className={styles.headerInfoTitle}>{narrative?.name}</h3>
          {showUpdatedTimeString && !isCaseManual ? (
            <div className={styles.updateTimeInfo}>
              <UpdateIcon
                className={styles.updateIcon}
                onClick={() => getNarrativeUpdateTime(narrative)}
              />
              {showUpdatedTimeString}
              <AnchorIcon
                className={styles.nextUpdateHint}
                data-tooltip-id={nextUpdateTooltipId}
                data-tooltip-content={nextUpdateTooltipContent}
              />
            </div>
          ) : (
            ''
          )}
        </div>
        <div className={styles.headerInfo}>
          <div className={styles.headerList}>
            <div className={styles.parametersInfoItem}>
              <span>{t('Start date')}:</span>{' '}
              {startDate ?? <>&ndash;</>}
            </div>
            <div className={styles.parametersInfoItem}>
              <span> {t('End date')}:</span>
              {endDate ?? <>&ndash;</>}
            </div>
            <div className={styles.parametersInfoItem}>
              <span>
                {narrative?.is_threat === true
                  ? t('Threat type')
                  : t('Case type')}
                :
              </span>
              {caseType}
            </div>
            <div className={styles.parametersInfoItem}>
              {narrative?.is_threat === true ? (
                ''
              ) : (
                <>
                  <span>{t('Status')}:</span>{' '}
                  <span className={`${mapStatusClasses[isNarrativeActive]}`}>
                    {narrative?.is_active ? t('Active') : t('Inactive')}
                  </span>
                  {!narrative?.is_active && narrative?.inactive_reason ? (
                    <span
                      className={styles.parametersInfoAnchor}
                      data-tooltip-id={`infoAnchorTooltip`}
                      data-tooltip-content={t(
                        mapInactiveNarrativeReason[narrative?.inactive_reason],
                      )}
                    >
                      <AnchorIcon />
                    </span>
                  ) : (
                    ''
                  )}
                  <Tooltip
                    id={`infoAnchorTooltip`}
                    place="top"
                    style={{ textTransform: 'none', fontSize: '14px' }}
                  />
                </>
              )}
            </div>
            {showPopulationStatus &&
            narrative?.population_status?.tm_calculation_status !==
              'NO_NEEDED' ? (
              <div className={styles.parametersInfoItem}>
                <span>{t('State')}:</span>
                {mapPopulationStatus[
                  narrative?.population_status?.tm_calculation_status
                ] || '-'}{' '}
              </div>
            ) : (
              ''
            )}
            {narrative?.type === 'IMPACT_ASSESSMENT' ? (
              <div className={styles.parametersInfoItem}>
                <span> {t('Link to content1')}:</span>
                {extractPlatformName(narrative.parameters.content_link)}
                <span>
                  <a
                    href={narrative.parameters.content_link}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.externalLink}
                  >
                    <LinkIcon />
                  </a>
                </span>
              </div>
            ) : (
              ''
            )}
          </div>
        </div>
        {narrative?.parameters?.keywords?.length > 0 ? (
          <div className={styles.keywordsContainer}>
            {formatQuery(keywordsString).map((token, index) => {
              if (token.text.trim().length > 0) {
                return (
                  <span
                    key={`${token.text}-${index}`}
                    style={{ color: token.color ? token.color : 'inherit' }}
                  >
                    {token.text}{' '}
                  </span>
                );
              } else {
                return '';
              }
            })}
          </div>
        ) : (
          ''
        )}
      </div>
      <Tooltip
        id={nextUpdateTooltipId}
        place="top"
        style={{ textTransform: 'none', fontSize: '14px' }}
      />
    </div>
  );
};
